import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Iconify } from '@/components';
import { VBMCardTitle, VBMEditButton } from '..';
import useResponsive from '@/hooks/useResponsive';
import usePostBookingSessionUpdate from '@/features/bookings-management/hooks/usePostBookingSessionUpdate';
import { BookingSession } from '@/features/bookings-management/types';

type VBMExtraHoursProps = {
  editable?: boolean;
  bookingSession?: BookingSession;
  sessionView?: boolean;
};

export default function VBMExtraHours({
  editable = false,
  bookingSession,
  sessionView = false,
}: VBMExtraHoursProps) {
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('sessionID');
  const downMd = useResponsive('down', 'md');
  const [isEdit, setIsEdit] = useState(false);
  const [sessionDto, setSessionDto] = useState({
    extra_hours: Number(bookingSession?.extra_hours ?? 0),
    extra_hours_description: bookingSession?.extra_hours_description || '',
  });

  const handleToggleEdit = (value: boolean) => setIsEdit(value);

  const handleExtraHoursChange = (value: string) => {
    const parsedValue = Number(value);
    const extraHours = Number.isNaN(parsedValue) ? 0 : Math.max(parsedValue, 0);

    setSessionDto((prevData) => ({
      ...prevData,
      extra_hours: extraHours,
      extra_hours_description:
        extraHours > 0 ? prevData.extra_hours_description : '',
    }));
  };

  const handleExtraHoursDescriptionChange = (value: string) => {
    setSessionDto((prevData) => ({
      ...prevData,
      extra_hours_description: value,
    }));
  };

  const onSuccessCallbackFn = () => {
    handleToggleEdit(false);
  };

  const { sessionUpdateMutation } = usePostBookingSessionUpdate({
    onSuccessFn: onSuccessCallbackFn,
    successText: 'Extra hours updated',
  });

  const handleSaveChanges = () => {
    sessionUpdateMutation.mutate({
      bookingSessionId: Number(sessionID),
      bookingSessionDto: {
        ...sessionDto,
        booking_cleaners:
          bookingSession?.booking_session_cleaners?.map(
            (cleaner) => cleaner.id
          ) ?? [],
      },
    });
  };

  const handleCancel = () => {
    setSessionDto({
      extra_hours: Number(bookingSession?.extra_hours ?? 0),
      extra_hours_description: bookingSession?.extra_hours_description || '',
    });
    handleToggleEdit(false);
  };

  const extraHours = Number(bookingSession?.extra_hours ?? 0);
  const extraHoursDescription = bookingSession?.extra_hours_description?.trim();
  const canEdit = editable && sessionView && !!bookingSession;
  const iconSize = downMd ? 20 : 30;

  return (
    <>
      <Stack sx={{ position: 'relative', py: 0.5 }} spacing={1}>
        {!isEdit && canEdit && (
          <VBMEditButton onClickButton={() => setIsEdit(true)} />
        )}
        <VBMCardTitle title="Extra Hours" />
        {isEdit ? (
          <Stack spacing={1.5}>
            <TextField
              type="number"
              size="small"
              value={sessionDto.extra_hours}
              onChange={(e) => handleExtraHoursChange(e.target.value)}
              inputProps={{ min: 0, step: 0.5 }}
              sx={{ maxWidth: 180 }}
            />
            <TextField
              label="Extra hours description"
              size="small"
              multiline
              minRows={2}
              value={sessionDto.extra_hours_description}
              onChange={(e) =>
                handleExtraHoursDescriptionChange(e.target.value)
              }
              disabled={sessionDto.extra_hours <= 0}
              placeholder="Optional note about the extra cleaning time"
            />
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                onClick={handleSaveChanges}
                loading={sessionUpdateMutation.isLoading}
              >
                {downMd ? 'Save' : 'Save changes'}
              </LoadingButton>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={0.75} sx={{ pl: 1, pt: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 35,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon="basil:clock-outline" width={iconSize} />
              </Box>
              <Typography fontSize={downMd ? 12 : 16}>
                {extraHours > 0 ? `${extraHours} hours` : 'No extra hours'}
              </Typography>
            </Stack>
            {extraHours > 0 && extraHoursDescription && (
              <Typography
                fontSize={downMd ? 12 : 14}
                color="text.secondary"
                sx={{ pl: downMd ? 0 : 5.75 }}
              >
                {extraHoursDescription}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
}
